/**
 * Permission Helper Functions
 *
 * Utilities for interpreting scopeType and dataScope in frontend components.
 * Provides clean abstractions for determining what locations and orgUnits
 * a user can access based on their permission scope.
 *
 * Scope Types:
 * - UNIVERSAL: User has access to all resources (empty arrays mean "all")
 * - CLIENT_SCOPED: User has access to all resources within their client
 * - ASSIGNMENT_SCOPED: User has access only to explicitly listed resources
 */

/**
 * Gets the list of accessible location IDs for a user
 *
 * @param {Object} dataScope - User's dataScope from permissions
 * @param {Array<string>} allLocations - Optional: All available locations (for UNIVERSAL scope)
 * @returns {Array<string>} Array of accessible location IDs
 *
 * @example
 * // UNIVERSAL scope
 * const dataScope = { scopeType: 'UNIVERSAL', accessibleLocations: [] };
 * getAccessibleLocations(dataScope, allLocations) // returns allLocations
 *
 * // ASSIGNMENT_SCOPED
 * const dataScope = { scopeType: 'ASSIGNMENT_SCOPED', accessibleLocations: ['loc1', 'loc2'] };
 * getAccessibleLocations(dataScope) // returns ['loc1', 'loc2']
 */
function getAccessibleLocations(dataScope, allLocations = []) {
  if (!dataScope || !dataScope.scopeType) {
    return [];
  }

  // Validate accessibleLocations is an array
  if (!Array.isArray(dataScope.accessibleLocations)) {
    return [];
  }

  switch (dataScope.scopeType) {
    case 'UNIVERSAL':
      // Empty arrays mean "all" for UNIVERSAL scope
      return allLocations || [];

    case 'CLIENT_SCOPED':
    case 'ASSIGNMENT_SCOPED':
      // Return the explicit list
      return dataScope.accessibleLocations || [];

    default:
      // Invalid scope type
      return [];
  }
}

/**
 * Gets the list of accessible orgUnit IDs for a user
 *
 * @param {Object} dataScope - User's dataScope from permissions
 * @param {Array<string>} allOrgUnits - Optional: All available orgUnits (for UNIVERSAL scope)
 * @returns {Array<string>} Array of accessible orgUnit IDs
 *
 * @example
 * const dataScope = { scopeType: 'CLIENT_SCOPED', accessibleOrgUnits: ['ou1', 'ou2'] };
 * getAccessibleOrgUnits(dataScope) // returns ['ou1', 'ou2']
 */
function getAccessibleOrgUnits(dataScope, allOrgUnits = []) {
  if (!dataScope || !dataScope.scopeType) {
    return [];
  }

  // Validate accessibleOrgUnits is an array
  if (!Array.isArray(dataScope.accessibleOrgUnits)) {
    return [];
  }

  switch (dataScope.scopeType) {
    case 'UNIVERSAL':
      // Empty arrays mean "all" for UNIVERSAL scope
      return allOrgUnits || [];

    case 'CLIENT_SCOPED':
    case 'ASSIGNMENT_SCOPED':
      // Return the explicit list
      return dataScope.accessibleOrgUnits || [];

    default:
      // Invalid scope type
      return [];
  }
}

/**
 * Checks if a user can access a specific location
 *
 * @param {string} locationId - Location ID to check
 * @param {Object} dataScope - User's dataScope from permissions
 * @returns {boolean} True if user can access the location
 *
 * @example
 * canAccessLocation('loc1', dataScope) // returns true/false
 */
function canAccessLocation(locationId, dataScope) {
  if (!locationId || !dataScope || !dataScope.scopeType) {
    return false;
  }

  switch (dataScope.scopeType) {
    case 'UNIVERSAL':
      // UNIVERSAL scope can access any location
      return true;

    case 'CLIENT_SCOPED':
    case 'ASSIGNMENT_SCOPED':
      // Check if location is in the accessible list
      if (!Array.isArray(dataScope.accessibleLocations)) {
        return false;
      }
      return dataScope.accessibleLocations.includes(locationId);

    default:
      // Invalid scope type
      return false;
  }
}

/**
 * Checks if a user can access a specific orgUnit
 *
 * @param {string} orgUnitId - OrgUnit ID to check
 * @param {Object} dataScope - User's dataScope from permissions
 * @returns {boolean} True if user can access the orgUnit
 *
 * @example
 * canAccessOrgUnit('ou1', dataScope) // returns true/false
 */
function canAccessOrgUnit(orgUnitId, dataScope) {
  if (!orgUnitId || !dataScope || !dataScope.scopeType) {
    return false;
  }

  switch (dataScope.scopeType) {
    case 'UNIVERSAL':
      // UNIVERSAL scope can access any orgUnit
      return true;

    case 'CLIENT_SCOPED':
    case 'ASSIGNMENT_SCOPED':
      // Check if orgUnit is in the accessible list
      if (!Array.isArray(dataScope.accessibleOrgUnits)) {
        return false;
      }
      return dataScope.accessibleOrgUnits.includes(orgUnitId);

    default:
      // Invalid scope type
      return false;
  }
}

/**
 * Determines if data should be filtered based on scope type
 * Returns false for UNIVERSAL (no filtering needed), true otherwise
 *
 * @param {Object} dataScope - User's dataScope from permissions
 * @returns {boolean} True if data needs to be filtered
 *
 * @example
 * if (shouldFilterByDataScope(dataScope)) {
 *   data = filterLocationsByDataScope(data, dataScope);
 * }
 */
function shouldFilterByDataScope(dataScope) {
  if (!dataScope || !dataScope.scopeType) {
    return false;
  }

  // UNIVERSAL scope doesn't need filtering
  return dataScope.scopeType !== 'UNIVERSAL';
}

/**
 * Filters an array of location objects by dataScope
 *
 * @param {Array<Object>} locations - Array of location objects with 'id' field
 * @param {Object} dataScope - User's dataScope from permissions
 * @returns {Array<Object>} Filtered array of locations
 *
 * @example
 * const locations = [
 *   { id: 'loc1', name: 'Store 1' },
 *   { id: 'loc2', name: 'Store 2' }
 * ];
 * const filtered = filterLocationsByDataScope(locations, dataScope);
 */
function filterLocationsByDataScope(locations, dataScope) {
  if (!Array.isArray(locations) || locations.length === 0) {
    return [];
  }

  if (!dataScope || !dataScope.scopeType) {
    return [];
  }

  // UNIVERSAL scope - return all locations
  if (dataScope.scopeType === 'UNIVERSAL') {
    return locations;
  }

  // For CLIENT_SCOPED and ASSIGNMENT_SCOPED, filter by accessible list
  if (!Array.isArray(dataScope.accessibleLocations)) {
    return [];
  }

  const accessibleSet = new Set(dataScope.accessibleLocations);

  return locations.filter(location => {
    return location.id && accessibleSet.has(location.id);
  });
}

/**
 * Filters an array of orgUnit objects by dataScope
 *
 * @param {Array<Object>} orgUnits - Array of orgUnit objects with 'id' field
 * @param {Object} dataScope - User's dataScope from permissions
 * @returns {Array<Object>} Filtered array of orgUnits
 */
function filterOrgUnitsByDataScope(orgUnits, dataScope) {
  if (!Array.isArray(orgUnits) || orgUnits.length === 0) {
    return [];
  }

  if (!dataScope || !dataScope.scopeType) {
    return [];
  }

  // UNIVERSAL scope - return all orgUnits
  if (dataScope.scopeType === 'UNIVERSAL') {
    return orgUnits;
  }

  // For CLIENT_SCOPED and ASSIGNMENT_SCOPED, filter by accessible list
  if (!Array.isArray(dataScope.accessibleOrgUnits)) {
    return [];
  }

  const accessibleSet = new Set(dataScope.accessibleOrgUnits);

  return orgUnits.filter(orgUnit => {
    return orgUnit.id && accessibleSet.has(orgUnit.id);
  });
}

/**
 * Gets a human-readable label for a scope type
 *
 * @param {string} scopeType - Scope type enum value
 * @returns {string} Human-readable label
 *
 * @example
 * getScopeTypeLabel('UNIVERSAL') // returns 'Universal Access'
 */
function getScopeTypeLabel(scopeType) {
  switch (scopeType) {
    case 'UNIVERSAL':
      return 'Universal Access';
    case 'CLIENT_SCOPED':
      return 'Client-Wide Access';
    case 'ASSIGNMENT_SCOPED':
      return 'Assignment-Based Access';
    default:
      return 'Unknown Scope';
  }
}

/**
 * Checks if scope type is UNIVERSAL
 *
 * @param {string} scopeType - Scope type to check
 * @returns {boolean} True if UNIVERSAL
 */
function isScopeUniversal(scopeType) {
  return scopeType === 'UNIVERSAL';
}

/**
 * Checks if scope type is CLIENT_SCOPED
 *
 * @param {string} scopeType - Scope type to check
 * @returns {boolean} True if CLIENT_SCOPED
 */
function isScopeClientScoped(scopeType) {
  return scopeType === 'CLIENT_SCOPED';
}

/**
 * Checks if scope type is ASSIGNMENT_SCOPED
 *
 * @param {string} scopeType - Scope type to check
 * @returns {boolean} True if ASSIGNMENT_SCOPED
 */
function isScopeAssignmentScoped(scopeType) {
  return scopeType === 'ASSIGNMENT_SCOPED';
}

/**
 * Gets a summary of the data scope for display
 *
 * @param {Object} dataScope - User's dataScope from permissions
 * @returns {Object} Summary object with counts and details
 *
 * @example
 * const summary = getDataScopeSummary(dataScope);
 * // returns { scopeType: 'ASSIGNMENT_SCOPED', locationCount: 5, orgUnitCount: 2, ... }
 */
function getDataScopeSummary(dataScope) {
  if (!dataScope) {
    return {
      scopeType: null,
      scopeLabel: 'No Scope',
      locationCount: 0,
      orgUnitCount: 0,
      hasHierarchyExpansion: false
    };
  }

  const locationCount = Array.isArray(dataScope.accessibleLocations)
    ? dataScope.accessibleLocations.length
    : 0;

  const orgUnitCount = Array.isArray(dataScope.accessibleOrgUnits)
    ? dataScope.accessibleOrgUnits.length
    : 0;

  const hasHierarchyExpansion = Boolean(
    dataScope.includeChildren ||
    dataScope.includePeers ||
    dataScope.includeParent
  );

  return {
    scopeType: dataScope.scopeType,
    scopeLabel: getScopeTypeLabel(dataScope.scopeType),
    locationCount,
    orgUnitCount,
    hasHierarchyExpansion,
    includeChildren: dataScope.includeChildren || false,
    includePeers: dataScope.includePeers || false,
    includeParent: dataScope.includeParent || false,
    calculatedAt: dataScope.calculatedAt,
    calculatedFrom: dataScope.calculatedFrom
  };
}

// CommonJS exports
module.exports = {
  getAccessibleLocations,
  getAccessibleOrgUnits,
  canAccessLocation,
  canAccessOrgUnit,
  shouldFilterByDataScope,
  filterLocationsByDataScope,
  filterOrgUnitsByDataScope,
  getScopeTypeLabel,
  isScopeUniversal,
  isScopeClientScoped,
  isScopeAssignmentScoped,
  getDataScopeSummary
};
